import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, Minus } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { CONTACT } from "@/lib/schema";

export const FAQS = [
  {
    q: "What do you do?",
    a: "Bhaarat Wealth Group is a land investment advisory. We source high-conviction land parcels along India's infrastructure corridors, run full legal and title diligence, help you acquire the parcel in your own name, and advise on the exit. We are not a fund and we never pool investor capital. Each investor takes registered title to an identified parcel.",
  },
  {
    q: "How much capital have you deployed, and what return has it generated?",
    a: "To date we have closed 24 deals and deployed ₹92 Crore of investor capital, generating a 48% average return per annum. These are realized historical results, not projections or promises, and every underlying transaction is recorded at the government sub-registrar.",
  },
  {
    q: "What are your fees?",
    a: "We do not charge on the way in. Our compensation is structured to be earned at exit and is tied to investor profit. If a transaction does not perform, neither do we. Our incentive is aligned with yours.",
  },
  {
    q: "How do you select areas?",
    a: "Our proprietary ROCI Engine fuses three signals: registered sale transactions (real price momentum), government infrastructure tenders (a leading indicator), and CLU eligibility (a legal path from agricultural to commercial/residential use). A parcel must clear all three lenses plus a six-point on-ground clearance checklist before it reaches any investor.",
  },
  {
    q: "How do you remove legal land issues?",
    a: "Diligence is the core of what we do. We verify the title chain, encumbrance history, survey and boundary details, CLU status against the latest notification, and litigation history, validated on the ground at the farmer and tehsil-record level and through a network of state-specialist legal partners before any capital is deployed.",
  },
  {
    q: "Why do you only do land investments?",
    a: "Land in the path of sanctioned infrastructure has a rare combination: a real demand catalyst, a legal re-rating mechanism (CLU and FAR), transparent public price data, and it sits in your own name. It is tangible, verifiable, and its upside is permitted rather than merely hoped for.",
  },
  {
    q: "How can I verify your claims?",
    a: "Every deal we reference is recorded at the government sub-registrar's office and is independently checkable. We will walk you through exactly how to verify ownership, price and history yourself, and we will connect you with past investors before you commit.",
  },
  {
    q: "How long do I have to wait for a return?",
    a: "It depends entirely on the parcel and the corridor's timeline. This is a patient, milestone-driven strategy tied to infrastructure progress and CLU/FAR re-rating, not a fixed schedule. We never promise a timeline or a forward return.",
  },
];

export default function FaqSection({ eyebrow = "FAQ", heading = "Questions, answered straight." }) {
  return (
    <section className="bg-brand-paper py-24 lg:py-32" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <Reveal>
          <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">{eyebrow}</span>
          <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05] mb-12">
            {heading}
          </h2>
        </Reveal>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-brand-ink/15">
              <AccordionTrigger data-testid={`faq-trigger-${i}`}
                className="text-left font-display font-medium text-lg sm:text-xl text-brand-ink hover:no-underline hover:text-brand-orange py-6 [&>svg]:hidden group">
                <span className="flex items-center gap-4">
                  <span className="font-mono text-sm text-brand-grey">{String(i + 1).padStart(2, "0")}</span>
                  {f.q}
                </span>
                <span className="ml-auto shrink-0 relative w-5 h-5">
                  <Plus className="w-5 h-5 text-brand-orange absolute inset-0 group-data-[state=open]:opacity-0 transition-opacity" />
                  <Minus className="w-5 h-5 text-brand-orange absolute inset-0 opacity-0 group-data-[state=open]:opacity-100 transition-opacity" />
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-brand-grey text-[15px] leading-relaxed pb-6 pl-8 max-w-2xl">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <p className="mt-10 text-sm text-brand-grey">
          Still have a question? Write to{" "}
          <a href={`mailto:${CONTACT.email}`} className="text-brand-orange underline underline-offset-4">{CONTACT.email}</a>.
        </p>
      </div>
    </section>
  );
}
